import React, { PropsWithChildren, useCallback, useMemo, useRef } from "react";
import { SymbolsMetadataContext } from "@shared/features/common/SymbolsMetadata.context";
import { sharedLib } from "@shared/services/shared.service";
import { CONTRACTS_EXCHANGES } from "@shared/common/constants";
import { pickBy } from "lodash";
import { mapValues } from "lodash";
import { getAssetFromSymbol } from "@shared/utils/misc";
import { useSelector } from "react-redux";
import { selectSymbolsMetadata } from "@shared/slices/common.slice";
import { useClientsData } from "@shared/utils/hooks/misc";
import { ClientsMetadataManager } from "@shared/types";

const SymbolsMetadataProvider = ({ children }: PropsWithChildren) => {
  const getExchangeToSymbolsStatus =
    sharedLib.sharedApi.common.useGetSymbolsMetadataQuery(undefined);
  const symbolsMetadata = useSelector(selectSymbolsMetadata);
  const clientsData = useClientsData();
  const clientsDataRef = useRef<ClientsMetadataManager>();
  clientsDataRef.current = clientsData;
  const { isSuccess } = getExchangeToSymbolsStatus;

  const exchangeToSortedSymbols: Record<string, string[]> | undefined =
    useMemo(() => {
      if (symbolsMetadata) {
        return mapValues(symbolsMetadata, (val) => {
          return Object.keys(val).sort();
        });
      }
    }, [symbolsMetadata]);

  const exchangeToSymbols: Record<string, Set<string>> | undefined =
    useMemo(() => {
      if (exchangeToSortedSymbols) {
        return mapValues(exchangeToSortedSymbols, (val) => new Set(val));
      }
    }, [exchangeToSortedSymbols]);

  const exchangeToActiveSymbols: Record<string, string[]> | undefined =
    useMemo(() => {
      if (symbolsMetadata) {
        return mapValues(symbolsMetadata, (val) => {
          const validSymbols = pickBy(
            val,
            (symbolMetadata) => symbolMetadata.is_active
          );
          return Object.keys(validSymbols).sort();
        });
      }
    }, [symbolsMetadata]);

  const exchangeToActiveSymbolsSet: Record<string, Set<string>> | undefined =
    useMemo(
      () => mapValues(exchangeToActiveSymbols, (symbols) => new Set(symbols)),
      [exchangeToActiveSymbols]
    );

  const exchangeToActiveMicaSymbols: Record<string, string[]> | undefined =
    useMemo(() => {
      if (symbolsMetadata) {
        return mapValues(symbolsMetadata, (val) => {
          const validSymbols = pickBy(
            val,
            (symbolMetadata) =>
              symbolMetadata.mica_is_active !== false &&
              symbolMetadata.is_active
          );
          return Object.keys(validSymbols).sort();
        });
      }
    }, [symbolsMetadata]);

  const exchangeToActiveMicaSymbolsSet:
    | Record<string, Set<string>>
    | undefined = useMemo(
    () => mapValues(exchangeToActiveMicaSymbols, (symbols) => new Set(symbols)),
    [exchangeToActiveMicaSymbols]
  );

  const getSymbolExchanges = useCallback(
    (symbol?: string) => {
      if (!symbol) return [];
      return Object.keys(
        pickBy(exchangeToSymbols, (symbolsSet) => symbolsSet.has(symbol))
      );
    },
    [exchangeToSymbols]
  );

  const isSymbolActiveInAccount = useCallback(
    (symbol?: string, accountId?: number) => {
      const account = clientsDataRef.current?.getAccountById(accountId);
      if (!symbol || !accountId || !account) return;
      const isMicaAccount = clientsDataRef.current?.getIsMicaAccount(accountId);
      const symbolsMapping = isMicaAccount
        ? exchangeToActiveMicaSymbolsSet
        : exchangeToActiveSymbolsSet;
      return symbolsMapping?.[account.exchange]?.has(symbol);
    },
    [exchangeToActiveMicaSymbolsSet, exchangeToActiveSymbolsSet, clientsData]
  );

  const getSymbolMetadata = (symbol?: string, exchange?: string) => {
    if (!symbol || !exchange) return;
    return symbolsMetadata?.[exchange]?.[symbol];
  };

  const getMainCoin = (symbol?: string, exchange?: string) =>
    getSymbolMetadata(symbol, exchange)?.main_coin;

  const getQuantityCoin = (
    symbol?: string,
    exchange?: string,
    isLeftSideOnly?: boolean
  ) => {
    if (exchange && CONTRACTS_EXCHANGES.includes(exchange)) return "contracts";
    if (isLeftSideOnly && symbol) return getAssetFromSymbol(symbol);
    const mainCoin = getSymbolMetadata(symbol, exchange)?.main_coin;
    if (mainCoin) {
      return symbol?.replace(new RegExp("/?" + mainCoin + "/?", "gm"), "");
    }
  };

  const getConversionFormula = (symbol?: string, exchange?: string) =>
    getSymbolMetadata(symbol, exchange)?.amount_conversion_formula;

  const getExchangeSymbols = useCallback(
    (exchange?: string) => {
      return exchange && exchangeToSortedSymbols
        ? exchangeToSortedSymbols[exchange]
        : [];
    },
    [exchangeToSortedSymbols]
  );

  const getAccountActiveSymbols = useCallback(
    (clientId?: number) => {
      const account = clientsDataRef.current?.getAccountById(clientId);
      const isMicaAccount = clientsDataRef.current?.getIsMicaAccount(clientId);
      const symbolsMapping = isMicaAccount
        ? exchangeToActiveMicaSymbols
        : exchangeToActiveSymbols;
      if (!clientId || !account || !symbolsMapping) return;
      return symbolsMapping[account.exchange];
    },
    [exchangeToActiveSymbols, exchangeToActiveMicaSymbols, clientsData]
  );

  const contextValue = useMemo(
    () => ({
      symbolsMetadata,
      exchangeToSymbols,
      getSymbolExchanges,
      getExchangeToSymbolsStatus,
      isSymbolActiveInAccount,
      getSymbolMetadata,
      getMainCoin,
      getConversionFormula,
      getQuantityCoin,
      getExchangeSymbols,
      getAccountActiveSymbols,
      isSuccess,
    }),
    [symbolsMetadata, getAccountActiveSymbols]
  );

  return (
    <SymbolsMetadataContext.Provider value={contextValue}>
      {children}
    </SymbolsMetadataContext.Provider>
  );
};

export default React.memo(SymbolsMetadataProvider);
